import { Button, Card, EmptyState, Select, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AccountTabs } from '../../features/account/AccountTabs'
import { NotificationItem } from '../../features/notifications/NotificationItem'
import { useAccountProfile } from '../../lib/api/account'
import {
  type NotificationType,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../lib/api/notifications'
import { AccountHeader } from './profile'

export const Route = createFileRoute('/account/notifications')({
  component: NotificationsPage,
})

const TYPE_OPTIONS: readonly { value: '' | NotificationType; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'pickup_request', label: 'Pickup needed' },
  { value: 'take_approved', label: 'Take approved' },
  { value: 'take_submitted', label: 'Take submitted' },
  { value: 'comment_mention', label: 'Mentions' },
  { value: 'milestone_paid', label: 'Payment paid' },
  { value: 'milestone_due', label: 'Payment due' },
  { value: 'batch_ready_for_review', label: 'Batch ready' },
  { value: 'system', label: 'System' },
] as const

function NotificationsPage() {
  const profile = useAccountProfile()
  const toast = useToast()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [type, setType] = useState<'' | NotificationType>('')

  const filters = {
    limit: 100,
    unread: unreadOnly,
    ...(type ? { type } : {}),
  }

  const list = useNotifications(filters)
  const markOne = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const items = list.data?.items ?? []
  const isActor = profile.data?.primary_role === 'actor'

  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile.data} />
      <AccountTabs active="security" isActor={Boolean(isActor)}>
        <div className="flex flex-col gap-3">
          <Card
            title="Notifications"
            actions={
              <Button
                size="sm"
                variant="ghost"
                disabled={(list.data?.unread_count ?? 0) === 0}
                loading={markAll.isPending}
                onClick={() =>
                  markAll.mutate(undefined, {
                    onSuccess: (data) =>
                      toast.show({
                        severity: 'success',
                        title: `${data.marked} notification${data.marked === 1 ? '' : 's'} marked read`,
                      }),
                  })
                }
              >
                Mark all read
              </Button>
            }
          >
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <label className="flex flex-col gap-1 text-[13px] min-w-[180px]">
                <span className="font-medium">Type</span>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value as '' | NotificationType)}
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(e) => setUnreadOnly(e.target.checked)}
                />
                Unread only
              </label>
            </div>

            {list.isLoading ? (
              <div className="flex items-center gap-2 text-[var(--text-2)]">
                <Spinner /> Loading notifications...
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                title="Nothing here"
                description={
                  unreadOnly
                    ? "You're all caught up — no unread notifications."
                    : "You don't have any notifications yet."
                }
              />
            ) : (
              <div role="list" className="border border-[var(--border)] rounded">
                {items.map((n) => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    isMarking={markOne.isPending && markOne.variables === n.id}
                    onMarkRead={(id) => markOne.mutate(id)}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </AccountTabs>
    </section>
  )
}
